import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { Tables } from "@/types/supabase";
import {
  BarVisualizer,
  RoomAudioRenderer,
  RoomContext,
  useVoiceAssistant,
  VoiceAssistantControlBar,
} from "@livekit/components-react";
import "@livekit/components-styles";
import {
  ConnectionQuality,
  ConnectionState,
  DisconnectReason,
  Room,
  RoomEvent,
} from "livekit-client";
import {
  AlertCircle,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  Play,
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "../../../supabase/supabase";

// Type definitions
interface TestAgentDialogProps {
  assistantConfig: any;
  assistantId?: string;
}

type ConnectionDetails = {
  serverUrl: string;
  roomName: string;
  participantIdentity: string;
  token: string;
  assistant_id: any;
};

type CallEvent = {
  timestamp: string;
  type: string;
  description: string;
  status?: string;
};

/**
 * Call Event Log Component
 * Displays a list of call-related events with timestamps
 */
function CallEventLog({ events }: { events: CallEvent[] }) {
  return (
    <div className="mt-4 border rounded-md p-2 bg-gray-50 max-h-32 overflow-y-auto">
      <h3 className="text-sm font-medium mb-1">Call Events</h3>
      <div className="space-y-1">
        {events.map((event, index) => (
          <div key={index} className="text-xs flex items-start">
            <span className="text-gray-500 min-w-24">
              {new Date(event.timestamp).toLocaleTimeString()}
            </span>
            <span className={`font-medium ${getEventColor(event.type)}`}>
              {event.type}:
            </span>
            <span className="ml-1">{event.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Get color for event type
 */
function getEventColor(eventType: string): string {
  switch (eventType) {
    case "Initiated":
      return "text-blue-600";
    case "Dialing":
      return "text-amber-600";
    case "Ringing":
      return "text-orange-500";
    case "Connected":
      return "text-green-600";
    case "Disconnected":
      return "text-red-600";
    case "Error":
      return "text-red-700";
    default:
      return "text-gray-700";
  }
}

/**
 * Simple Voice Assistant Component
 * Shows audio visualization and agent state
 */
function SimpleVoiceAssistant() {
  const { state, audioTrack } = useVoiceAssistant();
  return (
    <div className="h-40">
      <BarVisualizer
        state={state}
        barCount={5}
        trackRef={audioTrack}
        style={{}}
      />
      <p className="text-center">{state}</p>
    </div>
  );
}

/**
 * LiveKit Voice Agent Component
 * Handles all LiveKit room connection and audio interactions
 */
function LiveKitVoiceAgent({
  connectionDetails,
}: {
  connectionDetails: ConnectionDetails;
}) {
  const [room] = useState(new Room());
  const [connected, setConnected] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [callStatus, setCallStatus] = useState<string>("initiated");
  const [callEvents, setCallEvents] = useState<CallEvent[]>([
    {
      timestamp: new Date().toISOString(),
      type: "Initiated",
      description: "Call setup started",
      status: "initiated",
    },
  ]);

  // Add an event to the call events log
  const addCallEvent = (type: string, description: string, status?: string) => {
    setCallEvents((prev) => [
      ...prev,
      {
        timestamp: new Date().toISOString(),
        type,
        description,
        status,
      },
    ]);

    // If status is provided, update the call status
    if (status) {
      setCallStatus(status);
    }
  };

  // Setup room event listeners
  useEffect(() => {
    // Setup all event listeners for room events to track call status
    room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
      switch (state) {
        case ConnectionState.Connecting:
          addCallEvent("Dialing", "Establishing connection to room", "dialing");
          break;
        case ConnectionState.Connected:
          addCallEvent(
            "Connected",
            "Successfully connected to room",
            "connected"
          );
          break;
        case ConnectionState.Disconnected:
          addCallEvent(
            "Disconnected",
            "Disconnected from room",
            "disconnected"
          );
          break;
      }
    });

    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      let description = "Call ended";
      if (reason) {
        description = `Call ended: ${DisconnectReason[reason]}`;
      }
      addCallEvent("Disconnected", description, "disconnected");
    });

    room.on(
      RoomEvent.ConnectionQualityChanged,
      (quality: ConnectionQuality) => {
        let qualityText = "unknown";
        switch (quality) {
          case ConnectionQuality.Excellent:
            qualityText = "excellent";
            break;
          case ConnectionQuality.Good:
            qualityText = "good";
            break;
          case ConnectionQuality.Poor:
            qualityText = "poor";
            break;
        }
        addCallEvent("Quality", `Connection quality changed to ${qualityText}`);
      }
    );

    // Track participant events
    room.on(RoomEvent.ParticipantConnected, () => {
      addCallEvent("Ringing", "Remote participant connected", "ringing");
    });

    room.on(RoomEvent.ParticipantDisconnected, () => {
      addCallEvent(
        "Disconnected",
        "Remote participant disconnected",
        "disconnected"
      );
    });

    room.on(RoomEvent.MediaDevicesError, (e: Error) => {
      addCallEvent("Error", `Media device error: ${e.message}`, "error");
    });

    // Cleanup all event handlers
    return () => {
      room.removeAllListeners();
    };
  }, [room]);

  // Connect to room when connectionDetails are available
  useEffect(() => {
    const connectToRoom = async () => {
      try {
        // Validate server URL before attempting connection
        console.log("Full connection details:", connectionDetails);

        if (!connectionDetails || !connectionDetails.serverUrl) {
          throw new Error("LiveKit server URL is missing");
        }

        // Try-catch specifically for URL construction
        let serverUrl;
        try {
          // Test URL construction first (this will catch the same error that causes LiveKit to fail)
          new URL(connectionDetails.serverUrl);
          serverUrl = connectionDetails.serverUrl;
          console.log("URL validation passed for:", serverUrl);
        } catch (urlError) {
          // The URL is invalid, try to fix it
          console.warn(
            "Invalid URL format:",
            connectionDetails.serverUrl,
            urlError
          );

          // Try to construct a valid URL by ensuring protocol
          if (connectionDetails.serverUrl.includes("localhost")) {
            // Local development usually uses ws://
            serverUrl = `ws://${connectionDetails.serverUrl.replace(
              /^(https?:\/\/|wss?:\/\/)?/,
              ""
            )}`;
          } else {
            // Production usually uses wss://
            serverUrl = `wss://${connectionDetails.serverUrl.replace(
              /^(https?:\/\/|wss?:\/\/)?/,
              ""
            )}`;
          }

          console.log("Fixed server URL to:", serverUrl);

          // Validate the fixed URL
          try {
            new URL(serverUrl);
            console.log("Fixed URL is valid:", serverUrl);
          } catch (e) {
            console.error("Could not fix URL, using hardcoded fallback");
            // Last resort - hardcoded example URL (won't work but prevents crash)
            serverUrl = "wss://example.livekit.cloud";
          }
        }

        addCallEvent("Dialing", "Connecting to LiveKit room", "dialing");
        console.log("Connecting to LiveKit room:", connectionDetails.roomName);
        console.log(
          "Final server URL for connection:",
          serverUrl,
          connectionDetails
        );

        // Connect with robustly validated URL
        await room.connect(
          connectionDetails.serverUrl,
          connectionDetails.token,
          {
            autoSubscribe: true,
          }
        );

        console.log("Connected to room, enabling microphone...");
        await room.localParticipant.setMicrophoneEnabled(true);
        setConnected(true);
        setMicEnabled(true);
        addCallEvent(
          "Connected",
          "Successfully connected and enabled microphone",
          "connected"
        );
        console.log(
          "Successfully connected to LiveKit room and enabled microphone"
        );
      } catch (error) {
        console.error("Failed to connect to LiveKit room:", error);
        addCallEvent("Error", `Connection error: ${error.message}`, "error");
        toast({
          title: "Connection Error",
          description: `Failed to connect to the agent room: ${error.message}`,
          variant: "destructive",
        });
      }
    };

    if (connectionDetails) {
      console.log("Connection details available, attempting to connect...");
      console.log("Connection details:", connectionDetails);
      // Connect with a slight delay to allow UI to update
      const timer = setTimeout(() => {
        connectToRoom();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [connectionDetails, room]);

  // Disconnect when component unmounts
  useEffect(() => {
    return () => {
      if (connected) {
        room.disconnect();
      }
    };
  }, [connected, room]);

  // Toggle microphone
  const toggleMicrophone = async () => {
    if (connected) {
      const newState = !micEnabled;
      await room.localParticipant.setMicrophoneEnabled(newState);
      setMicEnabled(newState);
      addCallEvent(
        "Microphone",
        newState ? "Microphone enabled" : "Microphone disabled"
      );
    }
  };

  // Manually disconnect call
  const disconnectCall = async () => {
    if (connected) {
      addCallEvent("Disconnected", "User manually ended call", "disconnected");
      room.disconnect();
      setConnected(false);
      setMicEnabled(false);
    }
  };

  return (
    <RoomContext.Provider value={room}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between p-2 bg-slate-800 rounded text-white">
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full ${
                callStatus === "dialing"
                  ? "bg-amber-400 animate-pulse"
                  : callStatus === "ringing"
                  ? "bg-orange-400 animate-pulse"
                  : callStatus === "connected"
                  ? "bg-green-400"
                  : callStatus === "disconnected"
                  ? "bg-red-400"
                  : callStatus === "error"
                  ? "bg-red-600"
                  : "bg-blue-400"
              }`}
            ></div>
            <div>
              Room: {connectionDetails.roomName}{" "}
              <span className="text-xs opacity-75">({callStatus})</span>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMicrophone}
              className={micEnabled ? "text-green-400" : "text-red-400"}
            >
              {micEnabled ? (
                <Mic className="h-4 w-4" />
              ) : (
                <MicOff className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={disconnectCall}
              className="text-red-400"
            >
              <PhoneOff className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-evenly ">
          <SimpleVoiceAssistant />
          <VoiceAssistantControlBar />
        </div>

        <RoomAudioRenderer />
        <CallEventLog events={callEvents} />
      </div>
    </RoomContext.Provider>
  );
}

/**
 * Main Test Agent Dialog Component
 */
export default function ImprovedTestAgentDialog({
  assistantConfig,
  assistantId: propAssistantId,
}: TestAgentDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [agentStatus, setAgentStatus] = useState<
    "idle" | "connecting" | "connected" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [assistantData, setAssistantData] =
    useState<Tables<"assistants"> | null>(null);
  const [connectionDetails, setConnectionDetails] =
    useState<ConnectionDetails | null>(null);

  // Fetch assistant data from Supabase if we have an ID
  useEffect(() => {
    const fetchAssistantFromUrl = async () => {
      // Use the assistant ID from props
      const assistantId = propAssistantId;

      if (assistantId) {
        try {
          const { data, error } = await supabase
            .from("assistants")
            .select("*")
            .eq("id", assistantId)
            .single();

          if (error) {
            console.error("Error fetching assistant:", error);
            return;
          }

          if (data) {
            setAssistantData(data);
          }
        } catch (error) {
          console.error("Error fetching assistant data:", error);
        }
      }
    };

    fetchAssistantFromUrl();
  }, [propAssistantId]);

  // Start test agent and get LiveKit connection details
  const startTestAgent = async () => {
    setIsLoading(true);
    setAgentStatus("connecting");
    setErrorMessage(null);

    try {
      // Get assistant ID from props, falling back to fetched data
      const assistantId = propAssistantId || assistantData?.id;

      // Get session for authentication
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
      }

      // Get Supabase details from environment
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Call the unified API endpoint for web sessions
      // We use the same /api/call endpoint but with is_web_session=true
      // Use CALL_SERVICE_URL as the base URL for API calls
      const callServiceUrl = import.meta.env.VITE_CALL_SERVICE_URL || "";
      console.log("Using Call Service URL:", callServiceUrl);

      const response = await fetch(`${callServiceUrl}/api/call`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          // Use apikey header for authentication
          "x-api-key": supabaseAnonKey,
        },
        body: JSON.stringify({
          ...(assistantId && { assistant_id: assistantId }),
          is_web_session: true,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get token: ${errorText}`);
      }

      // Get connection details including token
      const data = await response.json();

      // Log the full response data for debugging
      console.log("API Response:", data);
      console.log("Server URL in response:", data.livekit_url);

      // Set connection details
      setConnectionDetails({
        serverUrl: data.livekit_url,
        roomName: data.room_name,
        participantIdentity: data.participant_identity,
        token: data.livekit_token,
        assistant_id: data.assistant_id,
      });

      // Update status to connected
      setAgentStatus("connected");

      // Show success toast
      toast({
        title: "Agent Started",
        description:
          "Your test agent is now running. You can interact with it in the dialog.",
      });
    } catch (error: any) {
      console.error("Error starting test agent:", error);
      setAgentStatus("error");
      setErrorMessage(error.message || "Unknown error occurred");

      toast({
        title: "Error Starting Agent",
        description:
          error.message ||
          "There was a problem starting the test agent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Play className="h-4 w-4" />
          Test Agent
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Test Voice Agent</DialogTitle>
          <DialogDescription>
            Test your voice agent configuration in real-time. Click "Start
            Agent" to begin the test.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {agentStatus === "idle" && (
            <div className="flex flex-col items-center justify-center p-8 border rounded-md">
              <p className="mb-4 text-center">
                Click the button below to start testing your agent with the
                current configuration.
              </p>
              <Button
                onClick={startTestAgent}
                disabled={isLoading}
                className="bg-agent-primary hover:bg-agent-primary/80 text-black"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting Agent...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Start Agent
                  </>
                )}
              </Button>
            </div>
          )}

          {agentStatus === "connecting" && (
            <div className="flex flex-col items-center justify-center p-8 border rounded-md">
              <Loader2 className="h-12 w-12 animate-spin text-agent-primary mb-4" />
              <p className="text-center">
                Connecting to agent server and initializing voice agent...
              </p>
            </div>
          )}

          {agentStatus === "connected" && connectionDetails && (
            <div className="flex flex-col">
              <div className="bg-black text-green-400 p-2 rounded-t-md text-xs">
                Connected to LiveKit room: {connectionDetails.roomName}
              </div>
              <div className="p-4 border rounded-b-md">
                <LiveKitVoiceAgent connectionDetails={connectionDetails} />
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <p className="font-medium">Instructions:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Click "Allow" when prompted for microphone access</li>
                  <li>
                    Speak clearly into your microphone to interact with the
                    agent
                  </li>
                  <li>
                    The agent will respond based on your configuration settings
                  </li>
                  <li>
                    You can toggle the microphone on/off with the mic button
                  </li>
                  <li>Close this dialog when you're done testing</li>
                </ul>
              </div>
            </div>
          )}

          {agentStatus === "error" && (
            <div className="flex flex-col items-center justify-center p-8 border border-destructive rounded-md">
              <div className="flex items-center justify-center mb-4">
                <AlertCircle className="h-8 w-8 text-destructive mr-2" />
                <p className="text-destructive text-center font-semibold">
                  Error Starting Agent
                </p>
              </div>
              <p className="text-destructive mb-4 text-center">
                {errorMessage ||
                  "There was an error starting the test agent. Please check your configuration and try again."}
              </p>
              <div className="flex flex-col space-y-2 w-full max-w-md">
                <Button
                  onClick={startTestAgent}
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive/10"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
